import { useCallback, useEffect, useRef, useState } from 'react';

// To prevent flickering of the overlap indicator, we need to add a threshold.
const OVERLAP_THRESHOLD = 4;

/**
 * @returns {Object}
 * - isOverlapping: Boolean indicating if primary navigation is overlapping the secondary navigation
 * - primaryNavRef: Ref object for the primary navigation container
 * - secondaryNavRef: Ref object for the secondary navigation container
 */
export const useOverlapObserver = ({ isPlatformSideNav }: { isPlatformSideNav: boolean }) => {
  const primaryNavRef = useRef<HTMLDivElement | null>(null);
  const secondaryNavRef = useRef<HTMLDivElement | null>(null);

  const [isOverlapping, setIsOverlapping] = useState<boolean>(false);

  const checkOverlap = useCallback(() => {
    if (!primaryNavRef.current || !secondaryNavRef.current) return;

    const primaryNavRect = primaryNavRef.current.getBoundingClientRect();
    const secondaryNavRect = secondaryNavRef.current.getBoundingClientRect();

    const isOverlapping = primaryNavRect.bottom >= secondaryNavRect.top - OVERLAP_THRESHOLD;

    setIsOverlapping(isOverlapping);
  }, []);

  useEffect(() => {
    if (!isPlatformSideNav) return;
    const resizeObserver = new ResizeObserver(checkOverlap);

    if (primaryNavRef.current) resizeObserver.observe(primaryNavRef.current);
    if (secondaryNavRef.current) resizeObserver.observe(secondaryNavRef.current);

    window.addEventListener('scroll', checkOverlap, { passive: true });
    window.addEventListener('resize', checkOverlap, { passive: true });

    checkOverlap();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', checkOverlap);
      window.removeEventListener('resize', checkOverlap);
    };
  }, [checkOverlap, isPlatformSideNav]);

  return { primaryNavRef, secondaryNavRef, isOverlapping };
};
